import axios from "axios";
import { MESSAGING_HTTP_BASE } from "@/config/messaging";
import { parseCourseCode } from "@/utils/parseCourseCode";
const courseCodeFromConversationId = (conversationId) => {
    // course:{schoolId}:{year}:{courseCode}
    const parts = String(conversationId || "").split(":");
    return parts.length >= 4 ? parts.slice(3).join(":") : "";
};
const descriptiveCourseTitle = (courseCode, fallbackName) => {
    const code = String(courseCode || "").trim();
    const fallback = String(fallbackName || "").trim();
    if (code) {
        try {
            return parseCourseCode(code).shortName;
        }
        catch {
            // continuar con fallback
        }
    }
    if (fallback && fallback.toUpperCase() !== code.toUpperCase())
        return fallback;
    return fallback || code || "Conversación";
};
const api = axios.create({ baseURL: MESSAGING_HTTP_BASE.replace(/\/$/, "") });
api.interceptors.request.use((config) => {
    const authToken = localStorage.getItem("authToken");
    if (authToken)
        config.headers.Authorization = authToken;
    return config;
});
const conversationPath = (conversationId) => `/conversations/${encodeURIComponent(conversationId)}`;
const previewOf = (lastMessage) => {
    if (!lastMessage)
        return "Sin mensajes";
    if (typeof lastMessage === "string")
        return lastMessage;
    if (typeof lastMessage === "object") {
        const item = lastMessage;
        return item.preview || item.body || "Sin mensajes";
    }
    return "Sin mensajes";
};
const courseCodeFromDirectId = (conversationId) => {
    // direct:{schoolId}:{year}:{courseCode}:{pairKey}
    const parts = String(conversationId || "").split(":");
    return parts[0] === "direct" && parts.length >= 5 ? parts[3] : "";
};
export const normalizeConversation = (raw) => {
    const conversationId = String(raw.conversationId ?? raw.id ?? "");
    const type = String(raw.type || "").toUpperCase();
    const isDirect = type === "DIRECT" || conversationId.startsWith("direct:");
    const isCustom = type === "CUSTOM" || conversationId.startsWith("custom:");
    const isSupport = type === "SUPPORT" || conversationId.startsWith("support:");
    const isAnnouncement = type === "ANNOUNCEMENT" || conversationId.startsWith("announcement:");
    const courseCodes = Array.isArray(raw.courseCodes)
        ? raw.courseCodes
            .map((code) => String(code || "").trim().toUpperCase())
            .filter(Boolean)
        : undefined;
    const courseCode = String(raw.courseId ??
        raw.courseCode ??
        courseCodes?.[0] ??
        (isDirect
            ? courseCodeFromDirectId(conversationId)
            : isCustom
                ? ""
                : courseCodeFromConversationId(conversationId)) ??
        "").trim();
    const rawName = String(raw.displayName ?? raw.title ?? raw.name ?? "").trim();
    // DIRECT/CUSTOM/SUPPORT/ANNOUNCEMENT: respetar displayName del backend.
    const title = isDirect || isCustom || isSupport || isAnnouncement
        ? rawName ||
            (isSupport ? "Soporte" : isAnnouncement ? "Avisos Mi\u00A0Centrum" : "") ||
            descriptiveCourseTitle(courseCode, rawName)
        : descriptiveCourseTitle(courseCode, rawName);
    const last = raw.lastMessage;
    const lastMessageSenderUserId = typeof last === "object" && last
        ? String(last.senderUserId || "").trim().toLowerCase() || null
        : null;
    const peerUserIds = Array.isArray(raw.peerUserIds)
        ? raw.peerUserIds
            .map((email) => String(email || "").trim().toLowerCase())
            .filter(Boolean)
        : undefined;
    return {
        id: conversationId,
        conversationId,
        title,
        name: title,
        courseCode: courseCode ||
            (isSupport
                ? "SP"
                : isAnnouncement
                    ? "AV"
                    : isDirect
                        ? "DM"
                        : isCustom
                            ? "CH"
                            : title.slice(0, 4)),
        type: raw.type,
        schoolId: raw.schoolId,
        year: raw.year,
        courseId: courseCode || undefined,
        courseCodes,
        audience: raw.audience,
        status: raw.status,
        lastMessage: previewOf(raw.lastMessage),
        lastMessageAt: typeof last === "object" && last ? last.createdAt || null : null,
        lastMessageSenderUserId,
        unread: Boolean(raw.unread),
        unreadCount: raw.unreadCount ?? 0,
        peerUserIds,
        muted: Boolean(raw.muted),
        mutedUntil: raw.mutedUntil ?? null,
        canHide: raw.canHide !== false,
        role: raw.role,
        requesterUserId: raw.requesterUserId ?? null,
        supportStatus: raw.supportStatus ?? null,
        claimedBy: raw.claimedBy ?? null,
        claimedAt: raw.claimedAt ?? null,
    };
};
export const setConversationHidden = async (conversationId, hidden = true) => {
    const { data } = await api.post(`${conversationPath(conversationId)}/hide?op=${hidden ? "hide" : "unhide"}`, { hidden });
    return data;
};
export const ensureMySupportConversations = async () => {
    const { data } = await api.post("?op=ensure-support", {});
    return data;
};
export const getMessagingMe = async () => {
    const { data } = await api.get("?op=messaging-me");
    return data;
};
export const broadcastSupportAnnouncement = async (body) => {
    const { data } = await api.post("?op=support-broadcast", { body });
    return data;
};
export const listSupportQueue = async (status = "OPEN,CLAIMED") => {
    const { data } = await api.get("?op=support-queue", {
        params: { status },
    });
    return { items: data?.items || [] };
};
export const claimSupportTicket = async (conversationId) => {
    const { data } = await api.post("?op=support-claim", { conversationId });
    return data;
};
export const resolveSupportTicket = async (conversationId, note) => {
    const { data } = await api.post("?op=support-resolve", {
        conversationId,
        note,
    });
    return data;
};
export const assignSupportTicket = async (conversationId, agentEmail) => {
    const { data } = await api.post("?op=support-assign", {
        conversationId,
        agentEmail,
    });
    return data;
};
export const setConversationMute = async (conversationId, payload) => {
    const { data } = await api.post(`${conversationPath(conversationId)}/mute?op=mute`, payload);
    return data;
};
export const getMessagingTicket = async () => {
    const { data } = await api.post("?op=ws-ticket");
    return data;
};
export const getConversations = async (year) => {
    const activeYear = String(year || new Date().getFullYear());
    const { data } = await api.get("?op=conversations", {
        params: { year: activeYear },
    });
    const list = Array.isArray(data)
        ? data
        : data?.items || data?.conversations || [];
    return list.map((item) => normalizeConversation(item));
};
export const getConversationMessages = async (conversationId, options = {}) => {
    const { data } = await api.get(`${conversationPath(conversationId)}/messages`, {
        params: options,
    });
    if (Array.isArray(data))
        return { items: data, receipts: undefined };
    return {
        items: data?.messages ?? data?.items ?? [],
        receipts: data?.receipts,
    };
};
export const getConversationParticipants = async (conversationId) => {
    const { data } = await api.get(`${conversationPath(conversationId)}/participants`);
    return data.participants ?? data.items ?? data;
};
export const getConversationParticipant = async (conversationId, userId) => {
    const email = String(userId || "")
        .trim()
        .toLowerCase();
    if (!email)
        return null;
    // Preferir query param: el email en path a menudo no llega bien por API Gateway.
    const { data } = await api.get(`${conversationPath(conversationId)}/participants`, { params: { userId: email } });
    const list = data.participants ?? data.items ?? (data.userId ? [data] : []);
    if (!Array.isArray(list) || !list.length)
        return null;
    const match = list.find((item) => String(item.userId || item.email || "")
        .trim()
        .toLowerCase() === email) || (list.length === 1 ? list[0] : null);
    return match || null;
};
export const getConversationSharedMedia = async (conversationId) => {
    const empty = { recent: [], lastWeek: [], older: [] };
    try {
        const { data } = await api.get(`${conversationPath(conversationId)}/shared`);
        return {
            media: data?.media || empty,
            documents: data?.documents || empty,
            links: data?.links || empty,
            totals: data?.totals || { media: 0, documents: 0, links: 0 },
        };
    }
    catch {
        // fallback op query si el proxy no expone /shared
        const { data } = await api.get("?op=shared", {
            params: { conversationId },
        });
        return {
            media: data?.media || empty,
            documents: data?.documents || empty,
            links: data?.links || empty,
            totals: data?.totals || { media: 0, documents: 0, links: 0 },
        };
    }
};
export const markConversationRead = async (conversationId, messageId, sortKey) => {
    await api.post(`${conversationPath(conversationId)}/read`, {
        messageId,
        ...(sortKey ? { sortKey } : {}),
    });
};
export const deleteMessage = async (conversationId, messageId) => {
    await api.post(`${conversationPath(conversationId)}/delete`, { messageId });
};
/** Crea/sincroniza chats COURSE del usuario según el año activo (idempotente). */
export const ensureMyCourseConversations = async (year) => {
    const activeYear = String(year || new Date().getFullYear());
    const { data } = await api.post("?op=ensure-my-courses", { year: activeYear }, { params: { year: activeYear } });
    return data;
};
/** Crea o reutiliza un chat 1:1 tipado al curso con otro integrante. */
export const ensureDirectConversation = async (input) => {
    const { data } = await api.post("?op=ensure-direct", {
        schoolId: input.schoolId,
        year: input.year,
        courseCode: input.courseCode,
        peerUserId: input.peerUserId,
    });
    return {
        ...normalizeConversation(data),
        peerDisplayName: data?.peerDisplayName,
    };
};
/** Crea un canal CUSTOM (temático del curso o entre directivas). */
export const createCustomConversation = async (input) => {
    const { data } = await api.post("?op=create-custom", {
        name: input.name,
        schoolId: input.schoolId,
        year: input.year,
        audience: input.audience,
        courseCodes: input.courseCodes,
    });
    return {
        ...normalizeConversation(data),
        memberCount: data?.memberCount,
    };
};
